import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { inputClass, labelClass, secondaryButtonClass } from '../components/formStyles';
import { PriceSourceBadge } from '../components/PriceSourceBadge';
import { formatJobMoney } from '../utils/jobPresentation';
import { formatSize } from '../utils/priceChart';
import { LineDraft, QuoteIssue, describeQuoteError, keepsStoredPrice, needsPriceQuote } from './jobLineDraft';
import { Toast } from '../components/Toast';

export type { LineDraft } from './jobLineDraft';
export { newLineDraft, validateLineDraft, linePayload, lineDraftFromLine } from './jobLineDraft';

interface Props {
  value: LineDraft;
  onChange: (next: LineDraft) => void;
  onRemove?: () => void;
  disabled?: boolean;
  title?: string;
  error?: string | null;
  /** The job's property: enables the automatic price preview. */
  propertyId?: string | null;
  /** The property's customer, for the "set the property size" link. */
  customerId?: string | null;
  /** Open the property link in a new tab (e.g. on the create page, where leaving would discard the draft job). */
  propertyLinkInNewTab?: boolean;
}

type QuoteState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; quote: types.PriceQuote }
  | { status: 'error'; issue: QuoteIssue };

function QuotePreview({ quote }: { quote: types.PriceQuote }) {
  const size = formatSize(quote.size, quote.unit);
  const converted =
    quote.chartUnit && quote.unit && quote.chartUnit !== quote.unit && quote.chartSize
      ? ` (${formatSize(quote.chartSize, quote.chartUnit)} on the chart)`
      : '';
  return (
    <p className="text-sm text-gray-700 flex flex-wrap items-center gap-2">
      <PriceSourceBadge source={quote.source} />
      <span>
        <span className="font-semibold">{formatJobMoney(quote.unitPrice)}</span> per unit
        {quote.source === 'CHART' && size ? ` at ${size}${converted}` : ''}
        {quote.source === 'CATALOG' ? ' (item price)' : ''}
      </span>
    </p>
  );
}

export function JobLineEditor({ value, onChange, onRemove, disabled = false, title = 'Work item', error, propertyId, customerId, propertyLinkInNewTab = false }: Props) {
  const api = useMemo(() => createAPIClient(), []);
  const [items, setItems] = useState<types.Item[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [quoteState, setQuoteState] = useState<QuoteState>({ status: 'idle' });

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.listItems(page, 50).then((response) => {
      if (!active) return;
      setItems((previous) => page === 1 ? response.data : [...previous, ...response.data.filter((item) => !previous.some((existing) => existing.id === item.id))]);
      setPages(Math.max(1, response.pagination.pages));
      setLoadError(null);
    }).catch((err) => {
      if (active) setLoadError(getErrorMessage(err, 'Failed to load catalog items'));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, page, reload]);

  // Ask the backend what the item would cost at this property. Pricing math
  // lives only in the backend; this is a preview of what saving will do.
  const shouldQuote = needsPriceQuote(value, propertyId);
  const itemId = value.itemId;
  useEffect(() => {
    if (!shouldQuote || !propertyId) { setQuoteState({ status: 'idle' }); return; }
    let active = true;
    setQuoteState({ status: 'loading' });
    api.quotePrice({ itemId, propertyId }).then((quote) => {
      if (active) setQuoteState({ status: 'ok', quote });
    }).catch((err) => {
      if (active) setQuoteState({ status: 'error', issue: describeQuoteError(err) });
    });
    return () => { active = false; };
  }, [api, shouldQuote, itemId, propertyId]);

  const selected = items.find((item) => item.id === value.itemId);
  const update = (patch: Partial<LineDraft>) => onChange({ ...value, ...patch });
  const hasOverride = value.unitPrice !== '';
  const propertyEditPath = customerId && propertyId ? `/customers/${customerId}/properties/${propertyId}/edit` : null;

  let preview: React.ReactNode = null;
  if (value.itemId) {
    if (hasOverride) {
      preview = <p className="text-sm text-gray-700 flex flex-wrap items-center gap-2"><PriceSourceBadge source="MANUAL" /><span><span className="font-semibold">{Number.isFinite(Number(value.unitPrice)) ? formatJobMoney(value.unitPrice) : value.unitPrice}</span> per unit (override)</span></p>;
    } else if (keepsStoredPrice(value)) {
      preview = value.originalPriceSource === 'MANUAL'
        ? <p className="text-sm text-gray-700 flex flex-wrap items-center gap-2"><PriceSourceBadge source="MANUAL" /><span>Keeps its manual price. Clearing the override does not switch a manual line back to automatic pricing; remove the line and add it again for that.</span></p>
        : <p className="text-sm text-gray-700 flex flex-wrap items-center gap-2"><PriceSourceBadge source={value.originalPriceSource ?? 'CATALOG'} /><span>Keeps its current price. Change the item or enter an override to re-price it.</span></p>;
    } else if (quoteState.status === 'loading') {
      preview = <p role="status" className="text-sm text-gray-500">Checking price...</p>;
    } else if (quoteState.status === 'ok') {
      preview = <QuotePreview quote={quoteState.quote} />;
    } else if (quoteState.status === 'error') {
      const { issue } = quoteState;
      preview = <p role="alert" className={`text-sm rounded-md p-3 ${issue.tone === 'warning' ? 'bg-amber-50 border border-amber-300 text-amber-900' : 'bg-red-50 text-red-800'}`}>
        {issue.message}
        {issue.linkToProperty && propertyEditPath && <>{' '}<Link to={propertyEditPath} className="font-semibold underline" {...(propertyLinkInNewTab ? { target: '_blank', rel: 'noreferrer' } : {})}>Edit the property{propertyLinkInNewTab ? ' (opens in a new tab)' : ''}</Link></>}
      </p>;
    }
  }

  return <fieldset className="min-w-0 border border-gray-200 rounded-lg p-3 sm:p-4 space-y-3">
    <legend className="font-semibold text-gray-900 px-1">{title}</legend>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="sm:col-span-2"><label className={labelClass} htmlFor={`${value.key}-item`}>Catalog item</label><select id={`${value.key}-item`} className={inputClass} value={value.itemId} disabled={disabled} onChange={(event) => { const item = items.find((candidate) => candidate.id === event.target.value); update({ itemId: event.target.value, itemName: item?.name || '', unitPrice: '' }); }}><option value="">Select an item</option>{value.itemId && !selected && <option value={value.itemId}>{value.itemName || 'Selected item'}</option>}{items.map((item) => <option key={item.id} value={item.id}>{item.name} ({item.priceChartId ? 'price chart' : formatJobMoney(item.unitPrice)})</option>)}</select>{page < pages && <button type="button" disabled={loading || disabled} onClick={() => setPage((current) => current + 1)} className="mt-2 inline-flex items-center min-h-[44px] sm:min-h-0 text-sm text-blue-700 hover:underline">{loading ? 'Loading...' : 'Load more items'}</button>}{loadError && <><Toast kind="error">{loadError}</Toast><p className="mt-1 text-sm text-gray-600 break-words">Items could not be loaded. <button type="button" className="underline py-2 sm:py-0" onClick={() => setReload((current) => current + 1)}>Retry</button></p></>}</div>
      <div><label className={labelClass} htmlFor={`${value.key}-quantity`}>Quantity</label><input id={`${value.key}-quantity`} className={inputClass} type="number" min="0.001" step="any" value={value.quantity} disabled={disabled} onChange={(event) => update({ quantity: event.target.value })} /></div>
      <div><label className={labelClass} htmlFor={`${value.key}-model`}>Pricing model</label><select id={`${value.key}-model`} className={inputClass} value={value.pricingModel} disabled={disabled} onChange={(event) => update({ pricingModel: event.target.value as types.JobPricingModel, quantity: event.target.value === 'FIXED' ? '1' : value.quantity })}><option value="PER_ITEM">Per item</option><option value="HOURLY">Hourly</option><option value="FIXED">Fixed</option></select></div>
      <div><label className={labelClass} htmlFor={`${value.key}-price`}>Unit price override</label><input id={`${value.key}-price`} className={inputClass} type="number" min="0" step="0.01" value={value.unitPrice} disabled={disabled} onChange={(event) => update({ unitPrice: event.target.value })} /><p className="text-xs text-gray-500 mt-1">Leave blank for automatic pricing (the item's price chart at the property's size, else its catalog price). Tax and totals are calculated when saved.</p></div>
    </div>
    {preview}
    {error && <Toast kind="error">{error}</Toast>}
    {onRemove && <button type="button" className={`${secondaryButtonClass} w-full sm:w-auto min-h-[44px] sm:min-h-0`} disabled={disabled} onClick={onRemove}>Remove item</button>}
  </fieldset>;
}
